import * as Snackbar from '@synergy/shared/snackbar/data-access';
import { APP_POOL_CONFIG } from '@synergy/config';
import { Dispatch } from 'redux';
import { EmployeePermissions } from '@synergy/models';
import { PaginatedResponse } from '@synergy/api';

import { Api } from '../../api';
import { AppState } from '../../store/models/app-state';
import { Contact, ContactMeta } from '../models/contact';
import { ContactRequest } from '../../features/contacts-logins/models/contact-request';
import { ErrorMessage } from '../api/models/enums/error-message';
import { LoginUser } from '../../features/contacts-logins/models/login-user';

export const CREATE_CONTACTS_LOGINS_COMPLETED = 'CREATE_CONTACTS_LOGINS_COMPLETED';
export const DELETING_CONTACT_COMPLETED = 'DELETING_CONTACT_COMPLETED';
export const PATCH_CONTACTS_LOGINS_COMPLETED = 'PATCH_CONTACTS_LOGINS_COMPLETED';
export const RECEIVE_ORDER_CEILINGS = 'RECEIVE_ORDER_CEILINGS';
export const RECEIVE_USERS = 'RECEIVE_USERS';
export const REQUEST_DATA = 'REQUEST_DATA';
export const RESET_USER = 'RESET_USER';
export const RESET_USER_MODAL = 'RESET_USER_MODAL';
export const START_CREATE_CONTACTS_LOGINS = 'START_CREATE_CONTACTS_LOGINS';
export const START_PATCH_CONTACTS_LOGINS = 'START_PATCH_CONTACTS_LOGINS';
export const UPDATE_STATE_USER = 'UPDATE_STATE_USER';
export const EMAIL_EXISTS_WARNING = 'EMAIL_EXISTS_WARNING';
export const DELETE_EMPLOYEE = 'DELETE_EMPLOYEE';
export const DELETE_EMPLOYEE_SUCCESS = 'DELETE_EMPLOYEE_SUCCESS';
export const DELETE_EMPLOYEE_FAILED = 'DELETE_EMPLOYEE_FAILED';
export const DELETE_EMPLOYEE_CONTACT_IS_ASSIGNED = 'DELETE_EMPLOYEE_CONTACT_IS_ASSIGNED';
export const DELETE_USER_LOGIN = 'DELETE_USER_LOGIN';
export const DELETE_USER_LOGIN_SUCCESS = 'DELETE_USER_LOGIN_SUCCESS';
export const DELETE_USER_LOGIN_FAILED = 'DELETE_USER_LOGIN_FAILED';
export const EMPLOYEE_PERMISSIONS_CHANGED = 'EMPLOYEE_PERMISSIONS_CHANGED';
export const BONUS_CONTACT_CHANGED = 'BONUS_CONTACT_CHANGED';

export const UPDATE_MAIN_CONTACT = 'UPDATE_MAIN_CONTACT';

const requestData = () => {
    return { type: REQUEST_DATA };
};

const receiveUsers = (
    paginatedUsers: PaginatedResponse<Contact | LoginUser, ContactMeta>,
    bonusContactCount: number
) => {
    return { type: RECEIVE_USERS, paginatedUsers, bonusContactCount };
};

const receiveOrderCeilings = (orderCeilings: number[]) => {
    return { type: RECEIVE_ORDER_CEILINGS, orderCeilings };
};

const startPatch = () => {
    return { type: START_PATCH_CONTACTS_LOGINS };
};

const patchCompleted = (user: Contact | LoginUser) => {
    return { type: PATCH_CONTACTS_LOGINS_COMPLETED, user };
};

const startCreate = () => {
    return { type: START_CREATE_CONTACTS_LOGINS };
};

const createCompleted = () => {
    return { type: CREATE_CONTACTS_LOGINS_COMPLETED };
};

export const resetUser = () => {
    return { type: RESET_USER };
};

export const resetUserModal = () => {
    return { type: RESET_USER_MODAL };
};

export const emailExistsError = () => {
    return { type: EMAIL_EXISTS_WARNING };
};

export const createUser = (newUser: ContactRequest) => {
    return async (dispatch: Dispatch, _getState: () => AppState, { api }: { api: Api }) => {
        dispatch(startCreate());
        const response = await api.contactsLogins.createUser(newUser);
        if (response.success) {
            dispatch(createCompleted());
        } else {
            if (response.error?.title === ErrorMessage.NonUnique) {
                dispatch(emailExistsError());
                return false;
            }
        }
        return true;
    };
};

export const patchUser = (user: ContactRequest) => {
    return async (dispatch: Dispatch, _getState: () => AppState, { api }: { api: Api }) => {
        dispatch(startPatch());
        const response = await api.contactsLogins.updateContact(user);
        if (response.success) {
            dispatch(patchCompleted(response.result));
        } else {
            if (response.error?.title === ErrorMessage.NonUnique) {
                dispatch(emailExistsError());
                return false;
            }
        }
        return true;
    };
};

export const deleteUserLogin = (user: Contact, assignTopicsToContactId?: number) => {
    return async (dispatch: Dispatch, _getState: () => AppState, { api }: { api: Api }) => {
        dispatch({ type: DELETE_USER_LOGIN });
        const response = await api.contactsLogins.deleteLoginUser(user);

        if (response.success) {
            dispatch({ type: DELETE_USER_LOGIN_SUCCESS, userId: user.id, assignTopicsToContactId });
            dispatch(Snackbar.enqueue(Snackbar.PoolNotification.success('app.snackbar.userDeleted')));
        } else {
            dispatch({ type: DELETE_USER_LOGIN_FAILED });
            dispatch(Snackbar.enqueue(Snackbar.PoolNotification.error('app.snackbar.userDeleteError')));
        }
    };
};

export const deleteEmployee = (
    userId: number,
    assignTopicsToContactId?: number,
    assignMainContactId?: number,
    assignBonusContactId?: number
) => {
    return async (dispatch: Dispatch, _getState: () => AppState, { api }: { api: Api }) => {
        dispatch({ type: DELETE_EMPLOYEE });
        const response = await api.contactsLogins.removeEmployee(
            userId,
            assignTopicsToContactId,
            assignMainContactId,
            assignBonusContactId
        );

        if (response.success) {
            dispatch({
                type: DELETE_EMPLOYEE_SUCCESS,
                userId,
                assignTopicsToContactId,
                assignMainContactId,
                assignBonusContactId,
            });
            dispatch(Snackbar.enqueue(Snackbar.PoolNotification.success('app.snackbar.employeeDeleted')));
        } else if (response.error?.title === ErrorMessage.ContactIsAssignedAsMainContact) {
            dispatch({ type: DELETE_EMPLOYEE_CONTACT_IS_ASSIGNED });
            dispatch(
                Snackbar.enqueue(
                    Snackbar.PoolNotification.error('app.snackbar.employeeContactIsAssigned', {
                        poolConfigNameShort: APP_POOL_CONFIG['name_short'],
                    })
                )
            );
        } else {
            dispatch({ type: DELETE_EMPLOYEE_FAILED });
            dispatch(Snackbar.enqueue(Snackbar.PoolNotification.error('app.snackbar.employeeDeleteError')));
        }
    };
};

export const fetchUsers = (searchParams: URLSearchParams) => {
    return async (dispatch: Dispatch, _getState: () => AppState, { api }: { api: Api }) => {
        dispatch(requestData());

        const response = await api.contactsLogins.cget(searchParams);

        if (response.success) {
            const bonusContactCount = response.result?.meta?.bonusContactCount ?? 0;
            dispatch(receiveUsers(response.result, bonusContactCount));
        }
    };
};

export const fetchOrderCeilings = () => {
    return async (dispatch: Dispatch, _getState: () => AppState, { api }: { api: Api }) => {
        const response = await api.contactsLogins.getOrderCeilings();

        if (response.success) {
            dispatch(receiveOrderCeilings(response.result));
        }
    };
};

export const updateEmployeePermissions = (user: Contact | LoginUser, permissions: EmployeePermissions[]) => {
    return async (dispatch: Dispatch, _getState: () => AppState, { api }: { api: Api }) => {
        const response = await api.contactsLogins.changeEmployeePermission(user, permissions);

        if (response.success) {
            dispatch({ type: EMPLOYEE_PERMISSIONS_CHANGED, user, permissions });
            dispatch(
                Snackbar.enqueue(
                    Snackbar.PoolNotification.success('app.snackbar.permission', {
                        permission: permissions.includes(EmployeePermissions.EmployeeOrders),
                    })
                )
            );
        }
    };
};

export const updateBonusContact = (
    user: Contact | LoginUser,
    isBonusContact: boolean,
    assignBonusContactId?: number
) => {
    return async (dispatch: Dispatch, _getState: () => AppState, { api }: { api: Api }) => {
        const response = await api.contactsLogins.updateBonusContact(user.id, isBonusContact, assignBonusContactId);

        if (response.success) {
            dispatch({ type: BONUS_CONTACT_CHANGED, user, isBonusContact });
            dispatch(
                Snackbar.enqueue(
                    Snackbar.PoolNotification.success('app.contacts.login.bonusContact.snackbar', {
                        isBonusContact,
                    })
                )
            );
        } else {
            dispatch(
                Snackbar.enqueue(Snackbar.PoolNotification.error('app.contacts.login.bonusContact.snackbarError'))
            );
        }
    };
};

export const updateMainContact = (mainContactId: number) => {
    return async (dispatch: Dispatch, _getState: () => AppState, { api }: { api: Api }) => {
        dispatch({ type: UPDATE_MAIN_CONTACT, mainContactId });
        await api.contactsLogins.updateMainContact(mainContactId);
    };
};
